import { Suspense, useState, useEffect, useContext } from "react";
import { ConfigProvider } from "antd";
import AppRouter from "./routes/AppRouter";
import SideBar from "./components/sideBar/sideBar";
import { AuthContext } from "../src/context/AuthContext";
import "./App.css";
import { ToastProvider } from "../src/context/ToastProvider";
import { ChatProvider } from "./context/ChatContext";
import ChatManager from "./components/chat/ChatManager";
import { useLocation } from "react-router-dom";
import { subscribeFleetDetails } from "./services/fleetDetailsFirestore";
import { t } from "i18next";
import { FleetAssistantProvider, useFleetAssistant } from "./context/FleetAssistantContext";
import FleetAssistantPanel from "./components/fleetAssistant/FleetAssistantPanel";
import FleetPlanBanner from "./components/billing/FleetPlanBanner";
import FleetUpdateNotice from "./components/FleetUpdateNotice";

function AppContent() {
  const location = useLocation();
  const isPublicPage = location.pathname.startsWith("/publicLink/"); // nova lógica
  const isStandalonePage =
    location.pathname.startsWith("/accept-invite") ||
    location.pathname.startsWith("/no-access") ||
    location.pathname.startsWith("/maintenance");
  const isLoginPage =
    location.pathname === "/" || location.pathname === "/login" || location.pathname === "/signup";
  const isDriverPage = location.pathname.startsWith("/drivers");
  const hideChat = isLoginPage || isDriverPage || isStandalonePage;
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [showNav, setShowNav] = useState(true);

  const { getFleetID } = useContext(AuthContext);
  const { isAssistantOpen } = useFleetAssistant();

  const [themeColors, setThemeColors] = useState({
    primary: "#1CE783",
    secondary: "#319795",
  });
  const [fleetLogo, setFleetLogo] = useState(null);

  const handleUpdateThemeColors = (type, value) => {
    setThemeColors((prev) => ({ ...prev, [type]: value }));
  };

  const handleUpdateFleetLogo = (newLogo) => {
    setFleetLogo(newLogo);
  };

  useEffect(() => {
    const fleetId = getFleetID();
    if (!fleetId) return undefined;

    const unsubscribe = subscribeFleetDetails(
      fleetId,
      (data) => {
        setThemeColors({
          primary: data?.primaryColor || "#1CE783",
          secondary: data?.secondaryColor || "#319795",
        });
        setFleetLogo(data?.logo || null);
      },
      (error) => {
        console.error("Error fetching fleet details from Firestore:", error);
      }
    );

    return () => unsubscribe();
  }, [getFleetID]);

  useEffect(() => {
    // Atualiza showNav quando a rota mudar
    const isPublicOrder = location.pathname.startsWith("/publicLink/");
    const isLoginPage =
      location.pathname === "/" || location.pathname === "/login" || location.pathname === "/signup";
    const isStandalone =
      location.pathname.startsWith("/accept-invite") ||
      location.pathname.startsWith("/no-access") ||
      location.pathname.startsWith("/maintenance");

    setShowNav(!isPublicOrder && !isLoginPage && !isStandalone);
  }, [location.pathname]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--chakra-colors-gandolapp-100", "#dffcee");
    root.style.setProperty("--chakra-colors-gandolapp-500", themeColors.primary);
    root.style.setProperty("--chakra-colors-gandolapp-secondary", themeColors.secondary);
    root.style.setProperty("--chakra-colors-gandolapp-neutral", "#B3B5B7");
    root.style.setProperty("--chakra-colors-gandolapp-standard", "#626262");
    root.style.setProperty("--chakra-colors-gandolapp-text", "#3F3F3F");
    root.style.setProperty("--chakra-colors-gandolapp-success", "#37a169");
    root.style.setProperty("--chakra-colors-gandolapp-warning", "#FFE173");
    root.style.setProperty("--chakra-colors-gandolapp-error", "#ff6565");
    root.style.setProperty("--chakra-colors-gandolapp-auxiliary", "#8A2BE2");
    root.style.setProperty("--chakra-colors-gandolapp-clean", "#FFF");
  }, [themeColors]);

  const canShowAssistant = !isPublicPage && !isLoginPage && !isStandalonePage;
  const showAssistantPanel = canShowAssistant && isAssistantOpen;
  const showSidebar = showNav;
  const effectiveSidebarExpanded = showAssistantPanel ? false : isSidebarExpanded;

  return (
    <Suspense fallback={t("common.loading", { defaultValue: "Loading..." })}>
      <div
        className={`app ${effectiveSidebarExpanded ? "sidebar-expanded" : "sidebar-collapsed"} ${
          showAssistantPanel ? "assistant-open" : ""
        }`}
      >
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: themeColors.primary,
              colorInfo: themeColors.secondary,
            },
          }}
        >
          <ChatProvider>
            <FleetUpdateNotice />
            <ToastProvider>
              {showSidebar && (
                <SideBar
                  isExpanded={effectiveSidebarExpanded}
                  lockCollapsed={showAssistantPanel}
                  onToggle={() => setIsSidebarExpanded(!isSidebarExpanded)}
                  fleetLogo={fleetLogo}
                />
              )}
              {showAssistantPanel ? <FleetAssistantPanel /> : null}
              <div
                className={`content-container ${
                  showSidebar
                    ? effectiveSidebarExpanded
                      ? "sidebar-expanded"
                      : "sidebar-collapsed"
                    : "no-sidebar"
                } ${isPublicPage ? "public-page" : ""} ${
                  showAssistantPanel ? "assistant-open-content" : ""
                }`}
              >
                {showSidebar && <FleetPlanBanner />}
                <AppRouter
                  funcNav={setShowNav}
                  themeColors={themeColors}
                  themeUpdater={handleUpdateThemeColors}
                  logoUpdater={handleUpdateFleetLogo}
                />
              </div>
              {!hideChat && <ChatManager />}
            </ToastProvider>
          </ChatProvider>
        </ConfigProvider>
      </div>
    </Suspense>
  );
}

export default function App() {
  return (
    <FleetAssistantProvider>
      <AppContent />
    </FleetAssistantProvider>
  );
}
